import type { FormatedProductPrice } from "@dransay/api";
import type { DeliveryMethodKind } from "@dransay/api/models";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFetcher } from "react-router";
import { useDebouncedCallback } from "~/hooks/use-debounced-callback.hook";
import type { FiltersState } from "../filters/types";
import { buildPdlExclusiveProductsFetchUrl } from "./exclusive-products-pdl.logic";

interface CuratedProductsFetcherResponse {
	products?: FormatedProductPrice[];
	error?: string;
}

interface UsePdlExclusiveProductsOptions {
	initialProducts: FormatedProductPrice[];
	vendorId: string | undefined;
	deliveryMethodKind: DeliveryMethodKind | undefined;
	filters: FiltersState;
}

function buildRequestKey(
	vendorId: string | undefined,
	deliveryMethodKind: DeliveryMethodKind | undefined,
	filters: FiltersState,
): string {
	return `${vendorId ?? ""}|${deliveryMethodKind ?? ""}|${JSON.stringify(filters)}`;
}

export function usePdlExclusiveProducts({
	initialProducts,
	vendorId,
	deliveryMethodKind,
	filters,
}: UsePdlExclusiveProductsOptions) {
	const fetcher = useFetcher<CuratedProductsFetcherResponse>();
	const [products, setProducts] = useState(initialProducts);
	const isInitialFetch = useRef(true);
	const lastFetchedKey = useRef<string | null>(null);
	const lastLoadedKey = useRef<string | null>(null);
	const requestKey = useMemo(
		() => buildRequestKey(vendorId, deliveryMethodKind, filters),
		[vendorId, deliveryMethodKind, filters],
	);
	const requestKeyRef = useRef(requestKey);
	requestKeyRef.current = requestKey;
	const debouncedLoad = useDebouncedCallback(
		(url: string, key: string) => {
			if (key !== requestKeyRef.current) {
				return;
			}
			lastFetchedKey.current = key;
			lastLoadedKey.current = key;
			fetcher.load(url);
		},
		{ delay: 200, trailing: true, leading: false },
	);
	useEffect(() => {
		setProducts(initialProducts);
		lastFetchedKey.current = null;
		lastLoadedKey.current = null;
		isInitialFetch.current = true;
	}, [initialProducts]);
	useEffect(() => {
		if (isInitialFetch.current) {
			isInitialFetch.current = false;
			lastFetchedKey.current = requestKey;
			return;
		}
		if (lastFetchedKey.current === requestKey) {
			debouncedLoad.cancel();
			return;
		}
		debouncedLoad.cancel();
		debouncedLoad(
			buildPdlExclusiveProductsFetchUrl(vendorId, deliveryMethodKind, filters),
			requestKey,
		);
	}, [requestKey, vendorId, deliveryMethodKind, filters, debouncedLoad]);
	useEffect(() => {
		if (fetcher.state !== "idle" || !fetcher.data?.products) {
			return;
		}
		if (lastLoadedKey.current !== requestKeyRef.current) {
			return;
		}
		setProducts(fetcher.data.products);
	}, [fetcher.state, fetcher.data]);
	const isLoading = fetcher.state === "loading";
	return { products, isLoading };
}
